import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';

const HOASignupSuccess = () => {
  const [status, setStatus] = useState('processing'); // processing, success, error
  const [message, setMessage] = useState('Processing your payment...');
  const [hoaData, setHoaData] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const hasRun = useRef(false); // Prevent double execution in React StrictMode

  useEffect(() => {
    // Prevent double execution
    if (hasRun.current) {
      console.log('⚠️ Skipping duplicate complete-signup call (already executed)');
      return;
    }
    hasRun.current = true;

    const completeSignup = async () => {
      try {
        // Get session_id from URL query params
        const params = new URLSearchParams(location.search);
        const sessionId = params.get('session_id');

        if (!sessionId) {
          setStatus('error');
          setMessage('Invalid payment session. Please contact support.');
          return;
        }

        console.log('✅ Payment successful! Completing HOA setup...');
        setMessage('Payment successful! Creating your HOA...');

        // Call backend to complete the HOA creation
        const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
        const response = await axios.post(
          `${backendUrl}/api/setup/complete-signup`,
          null,
          {
            params: { stripe_session_id: sessionId }
          }
        );

        if (response.data.success) {
          setStatus('success');
          setMessage('HOA created successfully!');
          setHoaData({
            hoa_id: response.data.hoa_id,
            hoa_code: response.data.hoa_code,
            board_member_credentials: response.data.board_member_credentials || []
          });

          // Don't auto-redirect - let user read and copy credentials
          // setTimeout(() => {
          //   navigate('/onboarding', { state: { hoaData: response.data } });
          // }, 3000);
        } else {
          throw new Error('Failed to complete HOA creation');
        }

      } catch (error) {
        console.error('❌ Error completing signup:', error);
        setStatus('error');
        setMessage(
          error.response?.data?.detail || 
          'Failed to complete HOA setup. Please contact support with your payment confirmation.'
        );
      }
    };

    completeSignup();
  }, []); // Empty dependency array - only run once on mount

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-2xl w-full">
        {status === 'processing' && (
          <div className="text-center">
            <div className="mx-auto h-20 w-20 mb-6">
              <svg className="animate-spin h-20 w-20 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Processing Payment</h2>
            <p className="text-gray-600">{message}</p>
            <div className="mt-6 space-y-2">
              <div className="flex items-center justify-center text-sm text-gray-500">
                <svg className="animate-pulse h-4 w-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Verifying payment...
              </div>
              <div className="flex items-center justify-center text-sm text-gray-500">
                <svg className="animate-pulse h-4 w-4 mr-2 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Creating your HOA...
              </div>
            </div>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center">
            <div className="mx-auto h-20 w-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
              <svg className="h-12 w-12 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">🎉 Success!</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            
            {hoaData && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-6 mb-6">
                <h3 className="font-semibold text-indigo-900 mb-3">Your HOA Details</h3>
                <div className="space-y-2 text-sm text-left">
                  <div className="flex justify-between">
                    <span className="text-indigo-700">HOA Code:</span>
                    <code className="bg-indigo-100 px-2 py-1 rounded font-mono">{hoaData.hoa_code}</code>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-indigo-700">HOA ID:</span>
                    <code className="bg-indigo-100 px-2 py-1 rounded font-mono text-xs">{hoaData.hoa_id}</code>
                  </div>
                </div>
              </div>
            )}
            {hoaData && hoaData.board_member_credentials && hoaData.board_member_credentials.length > 0 && (
              <div className="mt-4 p-4 bg-blue-50 border-2 border-blue-300 rounded-lg">
                <p className="font-bold text-blue-900 mb-3 text-lg">📧 Board Member Login Credentials</p>
                <p className="text-sm text-blue-800 mb-4">
                  ✅ A welcome email has been sent to each board member with their login information.
                </p>
                {hoaData.board_member_credentials.map((cred, idx) => (
                  <div key={idx} className="mt-3 p-3 bg-white rounded border border-blue-200">
                    <p className="text-sm"><strong>Name:</strong> {cred.name}</p>
                    <p className="text-sm"><strong>Email:</strong> {cred.email}</p>
                    <p className="text-sm"><strong>Temporary Password:</strong> <span className="font-mono bg-gray-100 px-2 py-1 rounded">{cred.password}</span></p>
                    <p className="text-sm"><strong>Title:</strong> {cred.title}</p>
                    <p className="text-xs text-gray-500 mt-2">⚠️ Must change password on first login</p>
                  </div>
                ))}
                <p className="text-sm text-blue-700 mt-4 font-semibold">
                  💾 Please save these credentials! Check your email for the full welcome message.
                </p>
              </div>
            )}

            <p className="text-sm text-gray-500 mb-4">
              Please save your HOA details above for future reference.
            </p>
            
            <button
              onClick={() => navigate('/onboarding', { state: { hoaData } })}
              className="px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
            >
              Continue to Onboarding
            </button>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="mx-auto h-20 w-20 bg-red-100 rounded-full flex items-center justify-center mb-6">
              <svg className="h-12 w-12 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Something Went Wrong</h2>
            <p className="text-gray-600 mb-6">{message}</p>
            
            <div className="space-y-3">
              <button
                onClick={() => navigate('/setup')}
                className="w-full px-6 py-3 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition"
              >
                Return to Setup
              </button>
              <button
                onClick={() => window.location.href = 'mailto:support@hoaplatform.com'}
                className="w-full px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition"
              >
                Contact Support
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HOASignupSuccess;
